// One tenant: what the registry knows (Overview, Config) and what its schema
// holds (Projects, Users). Config renders the raw jsonb — the backoffice
// shows what is stored, it does not maintain a parallel form for it.
// The two writes — add a user, add a site — open the same forms the
// onboarding stepper uses.
import { useState } from "react";
import { FolderKanban, Users } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { DataTable } from "@/components/gb/data-table";
import { EmptyState } from "@/components/gb/empty-state";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AnswerCounts } from "../chat/answer-counts";
import { TenantPromptsList } from "../prompts/tenant-prompts-page";
import { SiteCreateForm } from "../projects/site-form";
import { UserCreateForm } from "./user-form";
import { MicrosoftTab } from "./microsoft-tab";

/**
 * Whether this account may turn on the dashboard's debug view
 * (`architecture/0029`): the step records, links and model calls beside each
 * file, import and answer. A write, listed in `WRITE_ROUTES`.
 */
export function DebugToggle({
  tenantId,
  user,
}: {
  tenantId: string;
  user: { id: string; email: string; canDebug: boolean };
}) {
  const set = useSetUserDebug(tenantId);
  const on = set.isPending ? set.variables?.canDebug ?? user.canDebug : user.canDebug;
  return (
    <div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`Debug view for ${user.email}`}
        disabled={set.isPending}
        onClick={() => set.mutate({ userId: user.id, canDebug: !user.canDebug })}
        className={
          on
            ? "rounded-full border border-warn/60 bg-warn-bg px-2.5 py-0.5 text-[0.75rem] font-semibold text-warn-text"
            : "rounded-full border border-border bg-surface px-2.5 py-0.5 text-[0.75rem] font-semibold text-muted"
        }
      >
        {on ? "On" : "Off"}
      </button>
      {set.error && (
        <div role="alert" className="mt-1 text-[0.72rem] font-semibold text-danger">
          {set.error.message}
        </div>
      )}
    </div>
  );
}

/** A plain count tile — the gb StatCard is compliance-toned, this is not. */
function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-card border-[1.5px] border-border bg-surface p-[16px_18px]">
      <div className="font-display text-[2rem] font-bold text-ink">{value}</div>
      <div className="text-[0.84rem] font-semibold text-muted">{label}</div>
    </div>
  );
}
import { TenantStatusPill } from "./tenant-status";
import { useSetUserDebug, useTenant, useTenantStats, useTenantUsers } from "./tenants-api";
import { useProjects } from "../projects/projects-api";

export function TenantDetailPage() {
  const { tenantId = "" } = useParams();
  const navigate = useNavigate();
  const tenant = useTenant(tenantId);
  const stats = useTenantStats(tenantId);
  const users = useTenantUsers(tenantId);
  const projects = useProjects(tenantId);
  const [dialog, setDialog] = useState<"user" | "site" | null>(null);

  if (tenant.isError) {
    return <EmptyState icon={FolderKanban} title="Tenant not found" />;
  }
  if (!tenant.data) return null;
  const t = tenant.data;

  return (
    <div>
      <PageHeader
        eyebrow="Tenant"
        title={t.name}
        actions={<TenantStatusPill status={t.status} />}
      />
      <div className="mt-1 font-mono text-[0.75rem] text-faint">
        {t.slug} · {t.emailDomains.join(", ") || "no email domains"}
      </div>

      <Tabs defaultValue="overview" className="mt-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="answers">Answers</TabsTrigger>
          <TabsTrigger value="prompts">Prompts</TabsTrigger>
          <TabsTrigger value="microsoft">Microsoft 365</TabsTrigger>
          <TabsTrigger value="config">Config</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Metric label="Users" value={stats.data?.users ?? "…"} />
            <Metric label="Projects" value={stats.data?.projects ?? "…"} />
            <Metric label="Documents" value={stats.data?.documents ?? "…"} />
            <Metric label="Journal days" value={stats.data?.journalEntries ?? "…"} />
            <Metric label="Chat turns" value={stats.data?.chatMessages ?? "…"} />
          </div>
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button size="sm" onClick={() => setDialog("site")}>
              New site
            </Button>
          </div>
          <DataTable
            columns={[
              {
                key: "name",
                header: "Project",
                render: (p) => (
                  <div className="min-w-0">
                    <div className="truncate text-[0.9rem] font-semibold text-ink">{p.name}</div>
                    <div className="truncate font-mono text-[0.72rem] text-faint">
                      {[p.region, p.country, p.spec].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                ),
              },
              {
                key: "status",
                header: "Status",
                width: 160,
                render: (p) => (
                  <span className="font-mono text-[0.75rem] text-muted">{p.status}</span>
                ),
              },
            ]}
            rows={projects.data ?? []}
            getRowKey={(p) => p.id}
            onSelect={(p) => void navigate(`/tenants/${tenantId}/projects/${p.id}`)}
            emptyMessage="No projects."
          />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button size="sm" onClick={() => setDialog("user")}>
              Add user
            </Button>
          </div>
          {users.data && users.data.length === 0 ? (
            <EmptyState icon={Users} title="No accounts" />
          ) : (
            <DataTable
              columns={[
                {
                  key: "email",
                  header: "Account",
                  render: (u) => (
                    <div className="min-w-0">
                      <div className="truncate text-[0.85rem] text-ink">{u.email}</div>
                      <div className="truncate text-[0.72rem] text-faint">{u.name ?? "—"}</div>
                    </div>
                  ),
                },
                {
                  key: "debug",
                  header: "Debug view",
                  width: 150,
                  render: (u) => <DebugToggle tenantId={tenantId} user={u} />,
                },
                {
                  key: "created",
                  header: "Created",
                  width: 130,
                  render: (u) => (
                    <span className="font-mono text-[0.75rem] text-faint">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </span>
                  ),
                },
              ]}
              rows={users.data ?? []}
              getRowKey={(u) => u.id}
              emptyMessage="No accounts."
            />
          )}
        </TabsContent>

        {/* How the answering agent's answers ended (Phase 5, R119). */}
        <TabsContent value="answers" className="mt-4">
          <AnswerCounts tenantId={tenantId} />
        </TabsContent>

        <TabsContent value="prompts" className="mt-4">
          <TenantPromptsList tenantId={tenantId} />
        </TabsContent>

        {/* SharePoint sync's setup with the customer's admin (`plans/sharepoint-sync.md`). */}
        <TabsContent value="microsoft" className="mt-4">
          <MicrosoftTab
            tenantId={tenantId}
            organisation={(t.config.microsoft as { tenantId?: string } | null | undefined)?.tenantId ?? null}
          />
        </TabsContent>

        <TabsContent value="config" className="mt-4">
          <pre className="overflow-x-auto rounded-lg border border-border bg-white p-4 font-mono text-[0.78rem] leading-relaxed text-ink-prose">
            {JSON.stringify(t.config, null, 2)}
          </pre>
        </TabsContent>
      </Tabs>

      {/* Closing the user dialog is the last anyone sees of a just-generated
          password — the form says so before it is created. */}
      <Dialog open={dialog !== null} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{dialog === "user" ? "Add user" : "New site"}</DialogTitle>
          </DialogHeader>
          {dialog === "user" && <UserCreateForm tenantId={tenantId} />}
          {dialog === "site" && (
            <SiteCreateForm
              tenantId={tenantId}
              onCreated={(projectId) => {
                setDialog(null);
                void navigate(`/tenants/${tenantId}/projects/${projectId}`);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
